import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { WorkPartsFields } from './WorkPartsFields';
import { serializedPayload } from '../lib/serializedWork';
import FollowUpFields, { emptyFollowUp } from './FollowUpFields';
import InstallationPointMultiSelect from './InstallationPointMultiSelect';

const AddMaintenanceDialog = ({ open, onClose, machineryId, installationPoints = [], onSuccess, initialValues = null, sourceRequestId = null }) => {
  const [formData, setFormData] = useState({
    work_description: '',
    resolution: '',
    hours_spent: '',
    work_date: '',
    follow_up_required: '',
    notes: ''
  });
  const [installationPointIds, setInstallationPointIds] = useState([]);
  const [selectedParts, setSelectedParts] = useState([]);
  const [serializedParts, setSerializedParts] = useState([]);
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [followUp, setFollowUp] = useState(emptyFollowUp());
  const [imageFiles, setImageFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [imagePreviews, setImagePreviews] = useState([]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImageFiles(files);

    const previews = files.map((file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
    });

    Promise.all(previews).then(setImagePreviews);
  };

  const resetForm = () => {
    setSerializedParts([]);
    setRequestId(crypto.randomUUID());
    setFormData({
      work_description: '',
      resolution: '',
      hours_spent: '',
      work_date: '',
      follow_up_required: '',
      notes: '',
      ...(initialValues || {}),
    });
    setInstallationPointIds([]);
    setSelectedParts([]);
    setFollowUp(emptyFollowUp());
    setImageFiles([]);
    setImagePreviews([]);
  };

  useEffect(() => { if (open) resetForm(); }, [open, machineryId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      const imageUrls = [];
      for (const file of imageFiles) {
        const formDataImage = new FormData();
        formDataImage.append('file', file);
        const uploadRes = await axios.post(`${API}/upload-image`, formDataImage);
        imageUrls.push(uploadRes.data.image_url);
      }

      let workDateISO = null;
      if (formData.work_date) {
        workDateISO = new Date(formData.work_date).toISOString();
      }

      // Build parts_used display string + backend deduction payload from the picker.
      const partsUsedString = selectedParts
        .map((p) => (p.qty > 1 ? `${p.part_number} x${p.qty}` : p.part_number))
        .join(', ');

      const confirmedDeductions = selectedParts
        .filter((p) => !p.isNew)
        .map((p) => ({ part_number: p.part_number, qty: p.qty }));

      const newInventoryItems = selectedParts
        .filter((p) => p.isNew)
        .map((p) => ({ part_number: p.part_number, name: p.name }));

      const submitData = {
        request_id: requestId,
        source_request_id: sourceRequestId,
        serialized_actions: serializedPayload(serializedParts),
        machinery_id: machineryId || null,
        installation_point_ids: installationPointIds,
        work_description: formData.work_description,
        resolution: formData.resolution || null,
        hours_spent: parseFloat(formData.hours_spent),
        parts_used: partsUsedString,
        work_date: workDateISO,
        follow_up_required: formData.follow_up_required || null,
        notes: formData.notes || null,
        images: imageUrls,
        confirmed_deductions: confirmedDeductions,
        new_inventory_items: newInventoryItems,
      };

      if (followUp.enabled && followUp.description.trim()) {
        submitData.follow_up = {
          description: followUp.description.trim(),
          priority: followUp.priority || null,
          due_date: followUp.due_date
            ? new Date(followUp.due_date + 'T23:59:59').toISOString()
            : null,
          kind: followUp.kind || 'minor',
        };
      }

      const res = await axios.post(`${API}/maintenance-logs`, submitData);

      const totalUnits = confirmedDeductions.reduce((s, d) => s + (d.qty || 0), 0);
      const newCount = newInventoryItems.length;
      const parts = ['Maintenance log added'];
      if (totalUnits) parts.push(`${totalUnits} unit${totalUnits === 1 ? '' : 's'} deducted from inventory`);
      if (newCount) parts.push(`${newCount} new part${newCount === 1 ? '' : 's'} added (qty 0)`);
      toast.success(parts.join(' · '));

      onSuccess(res.data);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add maintenance log');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-maintenance-dialog">
        <DialogHeader>
          <DialogTitle>Add Maintenance Log</DialogTitle>
          <DialogDescription>Record maintenance work performed</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="work_description">Work Description/Issue *</Label>
            <Textarea
              id="work_description"
              data-testid="work-description-input"
              value={formData.work_description}
              onChange={(e) => setFormData({ ...formData, work_description: e.target.value })}
              placeholder="Describe the issue or work performed"
              rows={3}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="resolution">Resolution</Label>
            <Textarea
              id="resolution"
              data-testid="resolution-input"
              value={formData.resolution}
              onChange={(e) => setFormData({ ...formData, resolution: e.target.value })}
              placeholder="Describe how the issue was resolved"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="hours_spent">Hours Spent *</Label>
              <Input
                id="hours_spent"
                data-testid="hours-spent-input"
                type="number"
                step="0.1"
                value={formData.hours_spent}
                onChange={(e) => setFormData({ ...formData, hours_spent: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="work_date">Work Date & Time</Label>
              <Input
                id="work_date"
                data-testid="work-date-input"
                type="datetime-local"
                value={formData.work_date}
                onChange={(e) => setFormData({ ...formData, work_date: e.target.value })}
              />
            </div>
          </div>

          {installationPoints.length > 0 && (
            <div className="space-y-2">
              <Label>Installation Points (optional)</Label>
              <InstallationPointMultiSelect
                points={installationPoints}
                value={installationPointIds}
                onChange={setInstallationPointIds}
                testIdPrefix="add-log-ip"
              />
              <p className="text-xs text-slate-500">
                Pick every point this log covers — one PM may touch several
                (e.g. a paired-valve service). Any serialized part installed
                there at the time will attribute this log to itself.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <WorkPartsFields
              parts={selectedParts}
              onPartsChange={setSelectedParts}
              serialized={serializedParts}
              onSerializedChange={setSerializedParts}
              machineryId={machineryId}
              disabled={uploading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="follow_up_required">Follow-up notes (legacy text)</Label>
            <Textarea
              id="follow_up_required"
              data-testid="follow-up-textarea"
              value={formData.follow_up_required}
              onChange={(e) => setFormData({ ...formData, follow_up_required: e.target.value })}
              placeholder="Free-form notes (optional). Use the structured Follow-up section below for tracked follow-ups."
              rows={2}
            />
          </div>

          <FollowUpFields
            value={followUp}
            onChange={setFollowUp}
            testIdPrefix="add-log-follow-up"
          />

          <div className="space-y-2">
            <Label htmlFor="notes">Additional Notes</Label>
            <Textarea
              id="notes"
              data-testid="notes-input"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional observations or issues found"
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="images">Issue/Work Images (Optional)</Label>
            <Input
              id="images"
              data-testid="images-input"
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageChange}
            />
            {imagePreviews.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-2">
                {imagePreviews.map((preview, idx) => (
                  <img key={idx} src={preview} alt={`Preview ${idx + 1}`} className="h-24 rounded object-cover" />
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={uploading} data-testid="add-maintenance-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={uploading} data-testid="submit-log-btn">
              {uploading ? 'Adding...' : 'Add Log'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddMaintenanceDialog;
